import { useState } from 'react';
import { useLocation, Link } from 'react-router';
import { useTranslation } from 'react-i18next';

import {
    Box,
    Collapse,
    Divider,
    GlobalStyles,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    listItemButtonClasses,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
    Tooltip,
    TooltipProps,
    tooltipClasses,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const SidebarTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: theme.shadows[3],
        border: '1px solid',
        borderColor: theme.palette.divider,
        padding: 0,
    },
}));

import { getRoutesFlat } from 'src/app/AppRoutes';
import { PAGETYPE } from 'src/routesConfig';

import i18n from 'src/infrastructure/i18n/i18next.client';
import '../../node_modules/flag-icons/css/flag-icons.min.css';

export default function Sidebar() {
    const location = useLocation();

    const [isCollapsed, setIsCollapsed] = useState(false);

    return (
        <Paper
            className="Sidebar"
            sx={{
                position: { xs: 'fixed', md: 'sticky' },
                transform: {
                    xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))',
                    md: 'none',
                },
                transition: 'transform 0.4s, width 0.4s',
                height: '100dvh',
                width: 'var(--Sidebar-width)',
                top: 0,
                p: 2,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                borderRight: '1px solid',
                borderColor: 'divider',
                overflow: 'visible',
            }}
        >
            <IconButton
                onClick={() => setIsCollapsed(!isCollapsed)}
                sx={{
                    position: 'absolute',
                    top: 20,
                    right: -14,
                    zIndex: 10001,
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    width: 28,
                    height: 28,
                    '&:hover': {
                        backgroundColor: 'background.default',
                    },
                }}
                size="small"
            >
                {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>

            <GlobalStyles
                styles={(theme) => ({
                    ':root': {
                        '--Sidebar-width': isCollapsed ? '70px' : '220px',
                        [theme.breakpoints.up('lg')]: {
                            '--Sidebar-width': isCollapsed ? '70px' : '265px',
                        },
                    },
                })}
            />
            <Box
                className="Sidebar-overlay"
                sx={{
                    position: 'fixed',
                    zIndex: 9998,
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    opacity: 'var(--SideNavigation-slideIn)',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    transition: 'opacity 0.4s',
                    transform: {
                        xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))',
                        lg: 'translateX(-100%)',
                    },
                }}
            />
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    justifyContent: isCollapsed ? 'center' : 'initial',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                }}
            >
                <img
                    src={isCollapsed ? '/img/logo_without_text.svg' : '/img/logo_with_text.svg'}
                    alt="Logo"
                    style={{
                        width: isCollapsed ? '50%' : '70%',
                        transition: 'opacity 0.2s',
                    }}
                />
            </Box>

            <Box
                sx={{
                    minHeight: 0,
                    overflow: 'hidden auto',
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    [`& .${listItemButtonClasses.root}`]: {
                        gap: 1.5,
                    },
                }}
            >
                <List
                    dense
                    sx={{
                        gap: 1,
                        pl: 0,
                        flexGrow: 0,
                    }}
                >
                    {getSideBarListItems(
                        getRoutesFlat().filter((page: any) => page.handle?.type === PAGETYPE.MAIN),
                        location,
                        isCollapsed,
                    )}
                </List>

                <Divider />

                <List
                    dense
                    sx={{
                        gap: 1,
                        mt: 'auto',
                        flexGrow: 0,
                        pl: 0,
                        mb: 2,
                    }}
                >
                    {getSideBarListItems(
                        getRoutesFlat().filter((page: any) => page.handle?.type === PAGETYPE.FOOTER),
                        location,
                        isCollapsed,
                    )}
                </List>
            </Box>

            <Divider />

            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    opacity: isCollapsed ? 0 : 1,
                    transition: 'opacity 0.2s',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    visibility: isCollapsed ? 'hidden' : 'visible',
                }}
            >
                <div>
                    <a href="#" onClick={() => i18n.changeLanguage('de')}>
                        <span className="fi fi-de"></span>
                    </a>
                    &nbsp;
                    <a href="#" onClick={() => i18n.changeLanguage('en')}>
                        <span className="fi fi-us"></span>
                    </a>
                </div>
            </Box>
        </Paper>
    );
}

function getSideBarListItems(pages: Array<any>, location: any, isCollapsed: boolean = false) {
    return pages
        .filter((page) => !page.index && page.handle)
        .map((page) => (
            <SideBarListItem key={page.id + page.path} page={page} location={location} isCollapsed={isCollapsed} />
        ));
}

function SideBarListItem({ page, location, isCollapsed }: { page: any; location: any; isCollapsed: boolean }) {
    const { t } = useTranslation();

    const cleanPagePath = (page.path || '').replace(/^\/+|\/+$/g, '');
    const cleanLocationPath = location.pathname.replace(/^\/+|\/+$/g, '');

    const isNested: boolean = page?.children?.length > 0 && page.id !== 'homepage';

    // TODO implement properly - many ideas how this can fail
    const isSelected = Boolean(
        cleanPagePath === cleanLocationPath ||
            (cleanLocationPath && cleanPagePath && cleanLocationPath.endsWith(cleanPagePath)),
    );
    const isChildSelected = Boolean(cleanLocationPath && cleanPagePath && cleanLocationPath.startsWith(cleanPagePath));

    const content = isNested ? (
        <Toggler
            defaultExpanded={isChildSelected}
            isCollapsed={isCollapsed}
            renderToggle={({ open, setOpen }) => (
                <ListItemButton
                    onClick={() => {
                        if (!isCollapsed) {
                            setOpen(!open);
                        }
                    }}
                    sx={{
                        borderRadius: 1,
                        pl: isCollapsed ? 0 : 1,
                        pr: isCollapsed ? 0 : undefined,
                        justifyContent: isCollapsed ? 'center' : 'initial',
                        py: isCollapsed ? 1.5 : undefined,
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 24,
                            mr: isCollapsed ? 0 : 1,
                            justifyContent: 'center',
                            transform: isCollapsed ? 'scale(1.2)' : 'none',
                        }}
                    >
                        {page.handle.icon ? <>{page.handle.icon}</> : <AssignmentRoundedIcon />}
                    </ListItemIcon>
                    {!isCollapsed && (
                        <>
                            <ListItemText primary={<Typography variant="body2">{t(page.handle.title)}</Typography>} />
                            <KeyboardArrowDownIcon sx={{ transform: open ? 'rotate(180deg)' : 'none' }} />
                        </>
                    )}
                </ListItemButton>
            )}
        >
            <List dense sx={{ gap: 0.5, pl: 4 }}>
                {getSideBarListItems(page.children, location, isCollapsed)}
            </List>
        </Toggler>
    ) : (
        <ListItemButton
            selected={isSelected}
            role="menuitem"
            component={Link}
            to={page.path.replace(/:.*/g, '')}
            sx={{
                borderRadius: 1,
                pl: isCollapsed ? 0 : 1,
                pr: isCollapsed ? 0 : undefined,
                justifyContent: isCollapsed ? 'center' : 'initial',
                py: isCollapsed ? 1.5 : undefined,
            }}
        >
            {page.handle.icon && (
                <ListItemIcon
                    sx={{
                        minWidth: 24,
                        mr: isCollapsed ? 0 : 1,
                        justifyContent: 'center',
                        transform: isCollapsed ? 'scale(1.2)' : 'none',
                    }}
                >
                    {page.handle.icon}
                </ListItemIcon>
            )}
            {!isCollapsed && <ListItemText primary={<Typography variant="body2">{t(page.handle.title)}</Typography>} />}
        </ListItemButton>
    );

    if (isCollapsed) {
        return (
            <ListItem disablePadding sx={{ display: 'block' }}>
                <SidebarTooltip
                    title={
                        isNested ? (
                            <List dense sx={{ p: 0, m: 0 }}>
                                {getSideBarListItems(page.children, location, false)}
                            </List>
                        ) : (
                            <Box sx={{ p: 1 }}>{t(page.handle.title)}</Box>
                        )
                    }
                    placement="right-start"
                    disableInteractive={!isNested}
                    slotProps={{
                        popper: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, -8],
                                    },
                                },
                            ],
                            sx: {
                                zIndex: 11000,
                            },
                        },
                    }}
                >
                    <Box sx={{ display: 'block' }}>{content}</Box>
                </SidebarTooltip>
            </ListItem>
        );
    }

    return (
        <ListItem disablePadding sx={{ display: 'block' }}>
            {content}
        </ListItem>
    );
}

function Toggler({
    defaultExpanded = false,
    renderToggle,
    children,
    isCollapsed,
}: {
    defaultExpanded?: boolean;
    children: React.ReactNode;
    renderToggle: (params: {
        open: boolean;
        setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    }) => React.ReactNode;
    isCollapsed?: boolean;
}) {
    const [open, setOpen] = useState(defaultExpanded);
    return (
        <>
            {renderToggle({ open, setOpen })}
            <Collapse in={open && !isCollapsed} timeout="auto" unmountOnExit>
                {children}
            </Collapse>
        </>
    );
}
